import dynamic from "next/dynamic";
import type { ComponentType } from "react";
import { loadMessageBundle, type MessageBundle } from "@/i18n/messageBundles";
import { LOCALE_KEY } from "@/constants/app.constants";
import { resolveStoredLocale } from "@/providers/LocaleProvider";
import { WindowProps, WindowType } from "./types";
import { WindowSkeleton } from "./skeletons";
import { WINDOW_REGISTRY_METADATA, type WindowRegistryMetadata } from "./windowRegistry.metadata";
export { DOCK_APP_ORDER } from "./windowRegistry.metadata";
export function preloadMessageBundles(bundles: readonly MessageBundle[]) {
  try {
    const locale = resolveStoredLocale(localStorage.getItem(LOCALE_KEY));
    for (const bundle of bundles) {
      loadMessageBundle({
        locale,
        bundle
      }).catch(() => {
        // MessageBundleGate loads it again and shows the failure itself.
      });
    }
  } catch {
    // localStorage can throw in a locked-down browser; the gate still loads it.
  }
}
type WindowModule = {
  default: ComponentType<WindowProps>;
};
type LoadWindowModule = () => Promise<WindowModule>;
export function buildWindowLoader({
  load,
  bundles
}: {
  load: LoadWindowModule;
  bundles: readonly MessageBundle[];
}): LoadWindowModule {
  return () => {
    preloadMessageBundles(bundles);
    return load();
  };
}
export function lazyWindow({
  load,
  bundles
}: {
  load: LoadWindowModule;
  bundles: readonly MessageBundle[];
}): ComponentType<WindowProps> {
  return dynamic(buildWindowLoader({
    load,
    bundles
  }), {
    loading: () => <WindowSkeleton />,
    ssr: false // boolean-naming-exempt: external contract — Next.js dynamic() option
  });
}
interface WindowRegistryEntry extends WindowRegistryMetadata {
  load: ComponentType<WindowProps>;
}
function entryOf(type: WindowType, load: ComponentType<WindowProps>): WindowRegistryEntry {
  return {
    ...WINDOW_REGISTRY_METADATA[type],
    load
  };
}
export const WINDOW_REGISTRY: Record<WindowType, WindowRegistryEntry> = {
  wilson: entryOf("wilson", lazyWindow({
    load: () => import("./windows/WilsonWindow").then(mod => ({
      default: mod.WilsonWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.wilson.bundles
  })),
  comms: entryOf("comms", lazyWindow({
    load: () => import("./windows/comms/CommsWindow").then(mod => ({
      default: mod.CommsWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.comms.bundles
  })),
  settings: entryOf("settings", lazyWindow({
    load: () => import("./windows/SettingsWindow").then(mod => ({
      default: mod.SettingsWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.settings.bundles
  })),
  explorer: entryOf("explorer", lazyWindow({
    load: () => import("./windows/explorer/ExplorerWindow").then(mod => ({
      default: mod.ExplorerWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.explorer.bundles
  })),
  document: entryOf("document", lazyWindow({
    load: () => import("./windows/DocumentWindow").then(mod => ({
      default: mod.DocumentWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.document.bundles
  })),
  tasks: entryOf("tasks", lazyWindow({
    load: () => import("./windows/TodosWindow").then(mod => ({
      default: mod.TodosWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.tasks.bundles
  })),
  todos: entryOf("todos", lazyWindow({
    load: () => import("./windows/TodosWindow").then(mod => ({
      default: mod.TodosWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.todos.bundles
  })),
  setup: entryOf("setup", lazyWindow({
    load: () => import("./windows/SetupWindow").then(mod => ({
      default: mod.SetupWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.setup.bundles
  })),
  onboarding: entryOf("onboarding", lazyWindow({
    load: () => import("./windows/OnboardingWindow").then(mod => ({
      default: mod.OnboardingWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.onboarding.bundles
  })),
  diagramViewer: entryOf("diagramViewer", lazyWindow({
    load: () => import("./windows/DiagramViewerWindow").then(mod => ({
      default: mod.DiagramViewerWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.diagramViewer.bundles
  })),
  reports: entryOf("reports", lazyWindow({
    load: () => import("./windows/ReportsWindow").then(mod => ({
      default: mod.ReportsWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.reports.bundles
  })),
  report: entryOf("report", lazyWindow({
    load: () => import("./windows/ReportWindow").then(mod => ({
      default: mod.ReportWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.report.bundles
  })),
  meetWilson: entryOf("meetWilson", lazyWindow({
    load: () => import("./windows/MeetWilsonWindow").then(mod => ({
      default: mod.MeetWilsonWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.meetWilson.bundles
  })),
  knowledgeNote: entryOf("knowledgeNote", lazyWindow({
    load: () => import("./windows/KnowledgeNoteDetailWindow").then(mod => ({
      default: mod.KnowledgeNoteDetailWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.knowledgeNote.bundles
  })),
  knowledgeDoc: entryOf("knowledgeDoc", lazyWindow({
    load: () => import("./windows/KnowledgeDocDetailWindow").then(mod => ({
      default: mod.KnowledgeDocDetailWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.knowledgeDoc.bundles
  })),
  template: entryOf("template", lazyWindow({
    load: () => import("./windows/TemplateDetailWindow").then(mod => ({
      default: mod.TemplateDetailWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.template.bundles
  })),
  warehouseScheduling: entryOf("warehouseScheduling", lazyWindow({
    load: () => import("./windows/WarehouseSchedulingWindow").then(mod => ({
      default: mod.WarehouseSchedulingWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.warehouseScheduling.bundles
  })),
  skill: entryOf("skill", lazyWindow({
    load: () => import("./windows/skillDetail/SkillDetailWindow").then(mod => ({
      default: mod.SkillDetailWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.skill.bundles
  })),
  freightDashboard: entryOf("freightDashboard", lazyWindow({
    load: () => import("./windows/FreightDashboardWindow").then(mod => ({
      default: mod.FreightDashboardWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.freightDashboard.bundles
  })),
  cartagePay: entryOf("cartagePay", lazyWindow({
    load: () => import("./windows/CartagePayWindow").then(mod => ({
      default: mod.CartagePayWindow
    })),
    bundles: WINDOW_REGISTRY_METADATA.cartagePay.bundles
  }))
};